import React, { useRef, useState } from 'react';
import { Camera, Check, ImagePlus, Package, Palette, Trash2, X } from 'lucide-react';
import { adminApi, productsApi } from '../api';
import { Button } from './Button';
import { Modal } from './Modal';
import { useToast } from './Feedback';
import { ZONE_COLORS } from './RegionShape';
import './ZoneProducts.css';

// Product photos are only shown small, so they're shrunk further than shelf photos
const PRODUCT_PHOTO_EDGE = 1000;

// What's in a bin: a tile per SKU with its photo and name. Click a tile to see the photo large.
// products: { [sku]: { name, image_url, has_own_photo } } (see productsApi). With onProductChange,
// photos can be added, replaced and removed; it's called with (sku, newInfo). With onRemove, each
// tile gets a button to take that SKU out of the bin. With onStockChange, each tile has a count of
// how many are in the bin (stock: { [sku]: number }; empty = not counted).
export function ZoneProducts({
  skus, products = {}, onProductChange, onRemove, stock, onStockChange, emptyText = 'No products in this bin yet.',
}) {
  const toast = useToast();
  const fileInput = useRef(null);
  const [viewing, setViewing] = useState(null);
  const [uploadingSku, setUploadingSku] = useState(null);
  const [uploadTarget, setUploadTarget] = useState(null);
  const editable = Boolean(onProductChange);

  if (!skus || skus.length === 0) {
    return <p className="zone-products-empty">{emptyText}</p>;
  }

  const pickPhoto = (sku) => {
    setUploadTarget(sku);
    fileInput.current.value = '';
    fileInput.current.click();
  };

  const uploadPhoto = async (file) => {
    const sku = uploadTarget;
    if (!file || !sku) return;
    setUploadingSku(sku);
    try {
      const { url } = await adminApi.uploadImage(file, { maxEdge: PRODUCT_PHOTO_EDGE });
      const { sku: _sku, ...info } = await productsApi.setPhoto(sku, url);
      onProductChange(sku, info);
      toast.success(`Photo saved for ${sku}`);
    } catch (error) {
      toast.error(`Couldn't save the photo: ${error.message}`);
    } finally {
      setUploadingSku(null);
    }
  };

  const removePhoto = async (sku) => {
    setUploadingSku(sku);
    try {
      const { sku: _sku, ...info } = await productsApi.removePhoto(sku);
      onProductChange(sku, info);
      toast.success(`Photo removed from ${sku}`);
      if (!info.image_url) setViewing(null);
    } catch (error) {
      toast.error(`Couldn't remove the photo: ${error.message}`);
    } finally {
      setUploadingSku(null);
    }
  };

  const viewed = viewing && { sku: viewing, ...(products[viewing] || {}) };

  return (
    <>
      <ul className="zone-products">
        {skus.map(sku => {
          const info = products[sku] || {};
          const busy = uploadingSku === sku;
          return (
            <li key={sku} className="zone-product">
              <button
                type="button"
                className="zone-product-photo"
                onClick={() => (info.image_url ? setViewing(sku) : editable && pickPhoto(sku))}
                disabled={busy || (!info.image_url && !editable)}
                aria-label={info.image_url ? `View photo of ${info.name || sku}` : `Add a photo for ${sku}`}
              >
                {info.image_url ? (
                  <img src={info.image_url} alt="" loading="lazy" />
                ) : (
                  <span className="zone-product-placeholder">
                    {editable ? <ImagePlus size={20} aria-hidden="true" /> : <Package size={20} aria-hidden="true" />}
                    <span>{editable ? 'Add photo' : 'No photo'}</span>
                  </span>
                )}
                {busy && <span className="zone-product-busy" aria-hidden="true" />}
              </button>
              {onRemove && (
                <button
                  type="button"
                  className="zone-product-remove"
                  onClick={() => onRemove(sku)}
                  aria-label={`Take ${info.name || sku} out of this bin`}
                  title="Take out of this bin"
                >
                  <X size={14} aria-hidden="true" />
                </button>
              )}
              <span className={`zone-product-name ${info.name ? '' : 'is-unknown'}`}>
                {info.name || 'Not in any order yet'}
              </span>
              <span className="zone-product-sku mono">{sku}</span>
              {onStockChange && (
                <label className="zone-product-stock">
                  <span>In stock</span>
                  <input
                    type="number"
                    min="0"
                    inputMode="numeric"
                    className="input"
                    value={stock?.[sku] ?? ''}
                    placeholder="–"
                    onChange={(e) => onStockChange(sku, e.target.value === '' ? null : Math.max(0, parseInt(e.target.value, 10) || 0))}
                    aria-label={`How many ${info.name || sku} are in this bin`}
                  />
                </label>
              )}
            </li>
          );
        })}
      </ul>

      {editable && (
        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => uploadPhoto(e.target.files?.[0])}
        />
      )}

      <Modal
        open={Boolean(viewed)}
        onClose={() => setViewing(null)}
        title={viewed?.name || viewed?.sku}
        description={viewed?.name ? viewed.sku : undefined}
        footer={editable && viewed && (
          <>
            {viewed.has_own_photo && (
              <Button variant="ghost" icon={Trash2} onClick={() => removePhoto(viewed.sku)} loading={uploadingSku === viewed.sku}>
                Remove photo
              </Button>
            )}
            <Button icon={Camera} onClick={() => pickPhoto(viewed.sku)} disabled={uploadingSku === viewed.sku}>
              {viewed.has_own_photo ? 'Replace photo' : 'Use my own photo'}
            </Button>
            <Button variant="primary" icon={Check} onClick={() => setViewing(null)}>Done</Button>
          </>
        )}
      >
        {viewed?.image_url && (
          <div className="zone-product-viewer">
            <img src={viewed.image_url} alt={viewed.name || viewed.sku} />
          </div>
        )}
      </Modal>
    </>
  );
}

// Colour picker for a bin: the preset colours plus any colour from the system picker
export function ColorSwatches({ value, onChange, label = 'Bin colour' }) {
  const isPreset = ZONE_COLORS.some(c => c.value === (value || null));
  return (
    <div className="color-swatches" role="radiogroup" aria-label={label}>
      {ZONE_COLORS.map(color => {
        const selected = (value || null) === color.value;
        return (
          <button
            key={color.name}
            type="button"
            role="radio"
            aria-checked={selected}
            className={`color-swatch ${selected ? 'is-selected' : ''}`}
            style={{ '--swatch': color.value || 'var(--hl)' }}
            onClick={() => onChange(color.value)}
            title={color.name}
            aria-label={color.name}
          >
            {selected && <Check size={14} strokeWidth={3} aria-hidden="true" />}
          </button>
        );
      })}
      <label
        className={`color-swatch is-custom ${!isPreset ? 'is-selected' : ''}`}
        style={!isPreset ? { '--swatch': value } : undefined}
        title="Other colour"
      >
        <Palette size={14} aria-hidden="true" />
        <span className="sr-only">Other colour</span>
        <input type="color" value={value || '#ffe14d'} onChange={(e) => onChange(e.target.value)} />
      </label>
    </div>
  );
}
